'use client';

import { useState } from 'react';
import { getPremiumContent, PremiumCareerContent } from '@/lib/data/premiumContent';
import styles from './PremiumToolkit.module.css';

interface PremiumToolkitProps {
  careerId: string;
  careerTitle: string;
}

type TabId = 'interview' | 'salary' | 'employers' | 'linkedin';

export default function PremiumToolkit({ careerId, careerTitle }: PremiumToolkitProps) {
  const [activeTab, setActiveTab] = useState<TabId>('interview');
  const content: PremiumCareerContent = getPremiumContent(careerId);

  const tabs: { id: TabId; label: string; icon: string }[] = [
    { id: 'interview', label: 'Interview Prep', icon: '🎯' },
    { id: 'salary', label: 'Salary Tips', icon: '💰' },
    { id: 'employers', label: 'Employers', icon: '🏢' },
    { id: 'linkedin', label: 'LinkedIn', icon: '💼' },
  ];

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <span className={styles.icon}>🛠️</span>
        <h3>Complete Toolkit for {careerTitle}</h3>
      </div>

      <div className={styles.tabNav}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`${styles.tab} ${activeTab === tab.id ? styles.activeTab : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className={styles.tabIcon}>{tab.icon}</span>
            <span className={styles.tabLabel}>{tab.label}</span>
          </button>
        ))}
      </div>

      <div className={styles.tabContent}>
        {/* Interview Prep Tab */}
        {activeTab === 'interview' && (
          <div className={styles.interviewSection}>
            <p className={styles.sectionIntro}>
              Common interview questions for {careerTitle} roles. Practice these before your interview!
            </p>
            <div className={styles.questionsList}>
              {content.interview_questions.map((q, index) => (
                <div key={index} className={styles.questionCard}>
                  <div className={styles.questionNumber}>Q{index + 1}</div>
                  <div className={styles.questionContent}>
                    <p className={styles.questionText}>{q.question}</p>
                    <div className={styles.tipBox}>
                      <span className={styles.tipLabel}>💡 Tip:</span>
                      <span className={styles.tipText}>{q.tip}</span>
                    </div>
                    {q.sample_answer && (
                      <div className={styles.sampleAnswer}>
                        <span className={styles.sampleLabel}>Sample approach:</span>
                        <span className={styles.sampleText}>{q.sample_answer}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Salary Negotiation Tab */}
        {activeTab === 'salary' && (
          <div className={styles.salarySection}>
            <p className={styles.sectionIntro}>
              Negotiation strategies to maximize your offer for {careerTitle} positions.
            </p>
            <div className={styles.tipsList}>
              {content.salary_tips.map((tip, index) => (
                <div key={index} className={styles.salaryTipCard}>
                  <div className={styles.tipNumber}>{index + 1}</div>
                  <div className={styles.tipContent}>
                    <h4 className={styles.tipTitle}>{tip.title}</h4>
                    <p className={styles.tipDescription}>{tip.description}</p>
                    {tip.example && (
                      <div className={styles.exampleBox}>
                        <span className={styles.exampleLabel}>Example:</span>
                        <span className={styles.exampleText}>&ldquo;{tip.example}&rdquo;</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className={styles.coverLetterSection}>
              <h4 className={styles.subSectionTitle}>📝 Cover Letter Tips</h4>
              <ul className={styles.coverLetterTips}>
                {content.cover_letter_tips.map((tip, index) => (
                  <li key={index}>{tip}</li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Employers Tab */}
        {activeTab === 'employers' && (
          <div className={styles.employersSection}>
            <p className={styles.sectionIntro}>
              Companies actively hiring for {careerTitle} positions.
            </p>
            {content.employers.length > 0 ? (
              <div className={styles.employersList}>
                {content.employers.map((employer, index) => (
                  <div key={index} className={styles.employerCard}>
                    <div className={styles.employerHeader}>
                      <span className={styles.employerName}>{employer.name}</span>
                      <span className={`${styles.employerType} ${styles[`type${employer.type.replace(/\s+/g, '')}`]}`}>
                        {employer.type}
                      </span>
                    </div>
                    <span className={styles.employerIndustry}>{employer.industry}</span>
                    {employer.notes && (
                      <p className={styles.employerNotes}>{employer.notes}</p>
                    )}
                    {employer.hiring_url && (
                      <a
                        href={employer.hiring_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.employerLink}
                      >
                        View Jobs →
                      </a>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.noEmployers}>
                <p>Employer data for this career is being compiled. Check job boards:</p>
                <div className={styles.jobBoardLinks}>
                  <a href="https://indeed.com" target="_blank" rel="noopener noreferrer">Indeed</a>
                  <a href="https://linkedin.com/jobs" target="_blank" rel="noopener noreferrer">LinkedIn Jobs</a>
                  <a href="https://glassdoor.com" target="_blank" rel="noopener noreferrer">Glassdoor</a>
                </div>
              </div>
            )}
          </div>
        )}

        {/* LinkedIn Optimization Tab */}
        {activeTab === 'linkedin' && (
          <div className={styles.linkedinSection}>
            <p className={styles.sectionIntro}>
              Optimize your LinkedIn profile to attract {careerTitle} opportunities.
            </p>

            <div className={styles.keywordsSection}>
              <h4 className={styles.subSectionTitle}>🔍 Keywords to Include</h4>
              <p className={styles.keywordsIntro}>
                Add these keywords to your headline, summary, and experience sections:
              </p>
              <div className={styles.keywordTags}>
                {content.linkedin_keywords.map((keyword, index) => (
                  <span key={index} className={styles.keywordTag}>{keyword}</span>
                ))}
              </div>
            </div>

            <div className={styles.linkedinTips}>
              <h4 className={styles.subSectionTitle}>📈 Profile Optimization Tips</h4>
              <ul>
                <li>
                  <strong>Headline:</strong> Don&apos;t just list your current job. Include target role: &ldquo;Aspiring {careerTitle} | [Current Skills]&rdquo;
                </li>
                <li>
                  <strong>Summary:</strong> First 3 lines are visible without clicking &ldquo;See more&rdquo; - make them count with keywords and value proposition.
                </li>
                <li>
                  <strong>Skills:</strong> Add at least 10 relevant skills. Ask connections to endorse your top 3.
                </li>
                <li>
                  <strong>Activity:</strong> Engage with content in your target industry. Comment thoughtfully on posts from companies you want to work for.
                </li>
                <li>
                  <strong>Open to Work:</strong> Enable the &ldquo;Open to Work&rdquo; feature (recruiters only) to increase inbound opportunities.
                </li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
